import React from "react";
import type { Result, ResultCheck, CheckStatus } from "@sequesign/verifier-browser";
import type { BreakKind } from "../break.js";
import { SCENARIO } from "../scenario/torbridge.js";
import { LevelSummary, VerificationLevelLegend } from "./LevelBadges.js";

const CHECK_LABELS: Record<string, string> = {
  hash_integrity: "Hash integrity",
  sequence: "Sequence integrity",
  agent_binding: "Agent signature binding",
  schema_valid: "Evidence schema validation",
  genesis_mandate: "Mandate binding",
  conformance: "Mandate conformance",
  policy_binding: "Policy binding",
  witness_signature: "Witness countersignature",
  completeness: "Log completeness",
  inclusion_proofs: "Inclusion proofs",
  countersignature: "Human / counterparty countersignature",
  authorship: "Template authorship",
  receipt_structure: "Receipt structure"
};

const STATUS_LABEL: Record<CheckStatus, string> = {
  pass: "Pass",
  fail: "Fail",
  partial: "Partial",
  not_evaluated: "N/A"
};

function StatusPill({ status }: { status: CheckStatus }) {
  return <span className={`pill pill-${status}`}>{STATUS_LABEL[status]}</span>;
}

// Break labels + hints are client/narrative copy, so they live in SCENARIO
// (the templatization seam) rather than being hard-coded here.
const BREAK_LABELS: Record<BreakKind, string> = {
  "tamper-evidence": SCENARIO.breaks["tamper-evidence"].label,
  "tamper-schema": SCENARIO.breaks["tamper-schema"].label
};

const BREAK_HINTS: Record<BreakKind, string> = {
  "tamper-evidence": SCENARIO.breaks["tamper-evidence"].hint,
  "tamper-schema": SCENARIO.breaks["tamper-schema"].hint
};

export function VerificationPanel({
  result,
  loading,
  error,
  breaks,
  activeBreak,
  onBreak,
  onRestore
}: {
  result: Result | null;
  loading: boolean;
  error: string | null;
  breaks: BreakKind[];
  activeBreak: BreakKind | null;
  onBreak: (kind: BreakKind) => void;
  onRestore: () => void;
}) {
  return (
    <div className="verify-panel">
      <div className="verify-panel-head">
        <h3>Live verification</h3>
        <span className="verify-panel-sub">
          Recomputed in your browser with the real Sequesign verifier. Nothing is sent anywhere.
        </span>
      </div>

      {loading && <div className="verify-loading">Verifying…</div>}
      {error && <div className="verify-error">Verification error: {error}</div>}

      {result && !loading && (
        <>
          <div className={`verdict verdict-${result.valid ? "valid" : "invalid"}`}>
            <span className="verdict-mark" aria-hidden="true">
              {result.valid ? "✓" : "✕"}
            </span>
            <div>
              <div className="verdict-headline">
                {result.valid ? "Receipt verified" : "Verification failed"}
              </div>
              <div className="verdict-detail">
                {result.valid
                  ? conformanceLine(result)
                  : `Rejected: ${result.reason ?? "see the failed check below."}`}
              </div>
            </div>
          </div>

          {result.valid && <LevelSummary result={result} />}

          <ul className="check-list">
            {result.checks.map((c) => (
              <CheckRow key={c.id} check={c} />
            ))}
          </ul>

          <VerificationLevelLegend />

          <div className="facts-grid">
            <Fact label="Trust anchor">
              <TrustAnchor result={result} />
            </Fact>
            <Fact label="Mandate bound">
              {result.mandateBound === null
                ? "No mandate declared"
                : result.mandateBound
                  ? "Yes, bound to declared mandate"
                  : "No, mandate binding rejected"}
            </Fact>
            <Fact label="Conformance">
              {result.conformant === null
                ? "Not applicable"
                : result.conformant
                  ? "Conforms to the mandate"
                  : "Does NOT conform (violation recorded)"}
            </Fact>
            <Fact label="Approvals / counterparties">{legSummary(result)}</Fact>
          </div>

          {(result.approvals.length > 0 || result.counterparties.length > 0) && (
            <div className="attestations">
              {result.approvals.map((a) => (
                <div className="attestation" key={"a-" + a.id}>
                  <span className="attestation-kind">
                    {a.partyType === "human" ? "Human approval" : "Approval"}
                  </span>
                  <span className="attestation-signer">{a.signerId}</span>
                  <span className={"attestation-vouch " + (a.vouched ? "ok" : "self")}>
                    {a.vouched ? "vouched" : "self-asserted"}
                  </span>
                </div>
              ))}
              {result.counterparties.map((c) => (
                <div className="attestation" key={"c-" + c.id}>
                  <span className="attestation-kind">Counterparty</span>
                  <span className="attestation-signer">{c.signerId}</span>
                  <span className={"attestation-vouch " + (c.vouched ? "ok" : "self")}>
                    {c.vouched ? "vouched" : "self-asserted"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {breaks.length > 0 && (
        <div className="break-bar">
          <div className="break-bar-head">
            <strong>Break it</strong>
            <span>{SCENARIO.copy.breaksIntro}</span>
          </div>
          <div className="break-controls">
            {breaks.map((kind) => (
              <button
                key={kind}
                className={"break-btn" + (activeBreak === kind ? " break-btn-active" : "")}
                title={BREAK_HINTS[kind]}
                onClick={() => onBreak(kind)}
              >
                {BREAK_LABELS[kind]}
              </button>
            ))}
            <button className="restore-btn" disabled={activeBreak === null} onClick={onRestore}>
              Restore
            </button>
          </div>
          {activeBreak && <div className="break-note">{BREAK_HINTS[activeBreak]}</div>}
        </div>
      )}
    </div>
  );
}

function CheckRow({ check }: { check: ResultCheck }) {
  return (
    <li className={`check-row check-${check.status}`}>
      <div className="check-row-top">
        <span className="check-name">{CHECK_LABELS[check.id] ?? check.id}</span>
        <StatusPill status={check.status} />
      </div>
      <div className="check-detail">{check.detail}</div>
    </li>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="fact">
      <div className="fact-label">{label}</div>
      <div className="fact-value">{children}</div>
    </div>
  );
}

function TrustAnchor({ result }: { result: Result }) {
  if (result.trustAnchorMode === "self") {
    return (
      <span>
        self: integrity self-check
        <span className="fact-note">
          Verified against the witness key bundled inside the receipt, not an independent
          third-party anchor. A real auditor would anchor to the witness's published keys.
        </span>
      </span>
    );
  }
  if (result.trustAnchorMode === "external") {
    return <span>external: independent third-party anchor</span>;
  }
  return <span>none: no witness attestation</span>;
}

function conformanceLine(result: Result): string {
  if (result.conformant === false) {
    return "Structurally valid, but the recorded work does NOT conform to the mandate.";
  }
  if (result.conformant === true) {
    return "Integrity, signatures, schema and mandate conformance all check out.";
  }
  return "Integrity and signatures check out.";
}

function legSummary(result: Result): string {
  const parts: string[] = [];
  if (result.approvals.length > 0)
    parts.push(`${result.approvals.length} approval${result.approvals.length === 1 ? "" : "s"}`);
  if (result.counterparties.length > 0)
    parts.push(
      `${result.counterparties.length} counterparty confirmation${result.counterparties.length === 1 ? "" : "s"}`
    );
  return parts.length > 0 ? parts.join(", ") : "None";
}
